/**
 * THE DISPLAYED PRODUCT TITLE (operator ruling, 2026-10-10).
 *
 *   formatProductName({ name: 'Dendrobates Tinctorius Azureus',
 *     species: { genus: 'Dendrobates', species: 'Tinctorius',
 *                locality: 'Azureus', species_type: 0 } })
 *     -> 'Dendrobates tinctorius "Azureus"'
 *
 * A product's NAME is the operator's own text, and most begin with the
 * species' binomial typed with a capitalised epithet. When the product's
 * species is a KNOWN ANIMAL and the title's LEADING words equal its genus
 * + epithet ignoring case, exactly those words are replaced by the
 * species' proper form (`formatScientificName`); every other character is
 * left as typed, and a trailing text that IS the species' locality (in
 * any case or quote form) is printed in `formatLocality`'s form.
 * Otherwise — a plant, a type not known, a title that does not start with
 * its species, no species at all — the title is returned unchanged.
 *
 * ⚠️ ONE IMPLEMENTATION ON THE CLIENT, a declared PORT of
 * `store/services/product_names.display_product_title`;
 * `__tests__/productName.test.js` parses the server TABLE and asserts this
 * port row for row.
 *
 * ⚠️ DISPLAY ONLY. The stored name is never rewritten — an EDIT field
 * shows `product.name` as typed — and nothing here may reach a URL, a
 * slug, a lookup or a tag.
 */
import {
  formatScientificName, formatLocality, isKnownAnimalType,
} from './scientificName';

const QUOTES = '"\'“”‘’';

const words = (v) => (v === null || v === undefined ? '' : String(v))
  .split(/\s+/).filter(Boolean);

const stripQuotes = (s) => {
  let a = 0;
  let b = s.length;
  while (a < b && QUOTES.includes(s[a])) a += 1;
  while (b > a && QUOTES.includes(s[b - 1])) b -= 1;
  return s.slice(a, b);
};

const localityKey = (text) => stripQuotes(String(text || '').trim()).trim().toLowerCase();

/** The pure port: (stored title, the species' raw columns) -> displayed. */
export const formatProductTitle = (name, genus, species, locality, speciesType) => {
  const text = name === null || name === undefined ? '' : String(name);
  if (!isKnownAnimalType(speciesType)) return text;
  const genusWords = words(genus);
  const epithetWords = words(species);
  if (!genusWords.length || !epithetWords.length) return text;
  const want = [...genusWords, ...epithetWords];
  const found = [...text.matchAll(/\S+/g)];
  if (found.length < want.length) return text;
  for (let i = 0; i < want.length; i += 1) {
    if (found[i][0].toLowerCase() !== want[i].toLowerCase()) return text;
  }
  const head = text.slice(0, found[0].index);
  const last = found[want.length - 1];
  let rest = text.slice(last.index + last[0].length);
  const proper = formatScientificName(genus, species, null, speciesType);
  if (String(locality || '').trim() && rest.trim()
      && localityKey(rest) === localityKey(locality)) {
    const lead = rest.slice(0, rest.length - rest.trimStart().length);
    const tail = rest.slice(rest.trimEnd().length);
    rest = `${lead}${formatLocality(locality, speciesType)}${tail}`;
  }
  return `${head}${proper}${rest}`;
};

/**
 * The species a product payload carries: the nested `species` object
 * (ProductSerializer), else the flat genus / species / locality /
 * species_type fields (ProductVariationSerializer, cart lines).
 */
const speciesOf = (p) => {
  if (p.species && typeof p.species === 'object') return p.species;
  if (p.species_id && typeof p.species_id === 'object') return p.species_id;
  return {
    genus: p.genus, species: p.species, locality: p.locality,
    species_type: p.species_type,
  };
};

/**
 * `formatProductTitle` for a product payload. The title is `name`, else
 * `product_name`, else `title`, unless `opts.name` gives it; the species
 * may be given as `opts.species` when the payload does not carry one.
 */
export const formatProductName = (product, opts = {}) => {
  if (!product && !opts.name) return '';
  const p = product || {};
  const name = opts.name !== undefined ? opts.name
    : (p.name ?? p.product_name ?? p.title ?? '');
  const sp = opts.species || speciesOf(p) || {};
  const type = sp.species_type ?? p.species_type;
  return formatProductTitle(name, sp.genus, sp.species, sp.locality, type);
};

export default formatProductName;
